import { Component, OnInit, inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { BannerComponent } from './components/banner/banner.component';
import { CardModule } from 'primeng/card';
import { ProgressBarModule } from 'primeng/progressbar';
import { CardDestackCampanhaComponent } from '@app/shared/components/card-destack-campanha/card-destack-campanha.component';
import { CardCampanhaComponent } from '@app/shared/components/card-campanha/card-campanha.component';
import { PartnersComponent } from '@app/shared/components/partners/partners.component';
import { CampanhaDestaqueService } from '@app/shared/components/api/services/campanha-destaque.service';
import { CampanhaService } from '@app/shared/components/api/services/campanha.service';
import { CategoriaService } from '@app/shared/components/api/services/categoria.service';
import { Pagination } from '@app/shared/components/api/models/pagination.model';
import { forkJoin, of, catchError, map, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { DataVoidComponent } from '@app/shared/components/data-void/data-void.component';
import { SkeletonLoaderComponent } from '@app/shared/components/skeleton-loader/skeleton-loader.component';
import { UserService } from '@app/auth/services/user.service';
import { NewsletterComponent } from '@app/shared/components/newsletter/newsletter.component';
import { GestaoPaginaService } from '@app/shared/components/api/services/gestao-pagina.service';

import { TestemunhoService, TestemunhoItem } from '@app/shared/components/api/services/testemunho.service';
import { RouterModule } from '@angular/router';

@Component({
  selector: 'app-main-portal',
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    BannerComponent,
    CardModule,
    ProgressBarModule,
    CardDestackCampanhaComponent,
    CardCampanhaComponent,
    PartnersComponent,
    DataVoidComponent,
    SkeletonLoaderComponent,
    NewsletterComponent,
  ],
  providers: [CampanhaDestaqueService, CampanhaService, CategoriaService, TestemunhoService],
  templateUrl: './main-portal.component.html',
  styleUrl: './main-portal.component.scss',
  standalone: true,
})
export class MainPortalComponent implements OnInit, OnDestroy {
  private readonly campanhaDestaqueService = inject(CampanhaDestaqueService);
  private readonly campanhaService = inject(CampanhaService);
  private readonly categoriaService = inject(CategoriaService);
  private readonly userService = inject(UserService);
  private readonly gestaoPaginaService = inject(GestaoPaginaService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly testemunhoService = inject(TestemunhoService);

  campanhas: any;
  loading: boolean = false;
  callToAction: string = '';
  pagination: Pagination<any> = new Pagination<any>();

  // Normal Campaigns & Categories
  normalCampanhas: any[] = [];
  categoriaList: any[] = [];
  filtroCategoria: number | null = null;
  loadingNormais: boolean = false;
  totalCampanhasNormais: number = 0;

  // 2 Filtros da Página Inicial: Nome (Título) e Tipo de Financiamento
  searchQuery: string = '';
  filtroTipoFinanciamento: number | null = null;
  tiposFinanciamento = [
    { id: null, label: 'Todos os Tipos', icone: 'fas fa-layer-group' },
    { id: 1, label: 'Doação', icone: 'fas fa-hand-holding-heart' },
    { id: 2, label: 'Empréstimo', icone: 'fas fa-hand-holding-dollar' },
    { id: 3, label: 'Recompensa', icone: 'fas fa-gift' },
  ];
  private searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;

  // Testemunhos dinâmicos do backend
  testemunhos: TestemunhoItem[] = [];
  loadingTestemunhos: boolean = false;

  ngOnInit(): void {
    this.loadContents();
    this.getAllCampanhas();
    this.getCategorias();
    this.getCampanhasNormais();
    this.getTestemunhos();

    // Debounce na busca por nome da campanha
    this.searchSubscription = this.searchSubject
      .pipe(debounceTime(350), distinctUntilChanged())
      .subscribe(() => {
        this.getCampanhasNormais();
      });
  }

  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
  }

  onSearchInput(): void {
    this.searchSubject.next(this.searchQuery);
  }

  onSearchHome(): void {
    this.getCampanhasNormais();
  }

  limparBuscaHome(): void {
    this.searchQuery = '';
    this.getCampanhasNormais();
  }

  filtrarPorTipo(tipoId: number | null): void {
    this.filtroTipoFinanciamento = tipoId;
    this.getCampanhasNormais();
  }

  getTestemunhos(): void {
    this.loadingTestemunhos = true;
    const params = {
      PageSize: 6,
      PageNumber: 1,
      OrderBy: 'id desc',
      Ativo: true,
    };

    this.testemunhoService.list(params).subscribe({
      next: (res) => {
        const list: TestemunhoItem[] = res?.data || [];
        // Filtra estritamente apenas os ativos
        this.testemunhos = list.filter((t: TestemunhoItem) => t.ativo !== false);
        this.loadingTestemunhos = false;
      },
      error: (err) => {
        console.error('Erro ao listar testemunhos na página inicial:', err);
        this.testemunhos = [];
        this.loadingTestemunhos = false;
      },
    });
  }

  getStarsArray(count: number): number[] {
    return Array(Math.min(5, Math.max(0, count || 0))).fill(0);
  }

  getEmptyStarsArray(count: number): number[] {
    return Array(Math.max(0, 5 - Math.min(5, Math.max(0, count || 0)))).fill(0);
  }

  getUserInitials(name?: string): string {
    if (!name?.trim()) return 'FC';
    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  loadContents() {
    forkJoin({
      // Valores
      callToAction: this.gestaoPaginaService.getCallToAction(),
    }).subscribe((response) => {
      this.callToAction = response.callToAction ?? '';
    });
  }

  featuredCampaignIds: Set<number> = new Set<number>();

  getAllCampanhas() {
    this.loading = true;
    const params = {
      PageSize: 10,
      PageNumber: 1,
      OrderBy: 'id desc',
      EstadoCampanhaENUM: 4,
      Expirado: false,
      UtilizadorCurtiuID: this.userService.getUserId(),
    };

    this.campanhaDestaqueService.list(params).subscribe({
      next: (res) => {
        this.pagination = new Pagination<any>().deserialize(res);
        const campanhasDestaque = this.pagination?.data ?? res?.data ?? [];
        campanhasDestaque.forEach((d: any) => {
          const id = d?.campanha?.id || d?.campanhaId || d?.id;
          if (id) this.featuredCampaignIds.add(Number(id));
        });

        // Mark any currently loaded normal campaigns
        if (this.normalCampanhas?.length) {
          this.normalCampanhas.forEach((c: any) => {
            c.isDestaque = this.featuredCampaignIds.has(Number(c.id));
          });
        }
        this.loading = false;
      },
      error: (err) => {
        console.error('Erro ao listar campanhas em destaque', err);
        this.loading = false;
      },
    });
  }

  getCategorias(): void {
    this.categoriaService
      .listPublic({ pageSize: 100, CategoriaEmUso: true })
      .subscribe({
        next: (result) => {
          this.categoriaList = result?.data ?? [];
        },
        error: (err) => console.error('Erro ao listar categorias', err),
      });
  }

  filtrarPorCategoria(id: number | null): void {
    this.filtroCategoria = id;
    this.getCampanhasNormais();
  }

  getCampanhasNormais(): void {
    this.loadingNormais = true;
    const hasSearch = !!(this.searchQuery && this.searchQuery.trim());
    const params: any = {
      PageSize: hasSearch || this.filtroTipoFinanciamento ? 12 : 6,
      PageNumber: 1,
      OrderBy: 'id desc',
      EstadoCampanhaENUM: 4,
      Expirado: false,
      Randomizar: !hasSearch && !this.filtroTipoFinanciamento && !this.filtroCategoria,
      UtilizadorCurtiuID: this.userService.getUserId() || '',
    };

    if (this.filtroCategoria) {
      params.CategoriaId = this.filtroCategoria;
    }

    if (this.filtroTipoFinanciamento) {
      params.TipoFinanciamentoENUM = this.filtroTipoFinanciamento;
    }

    if (hasSearch) {
      const termo = this.searchQuery.trim();
      params.Pesquisa = termo;
      params.Titulo = termo;
    }

    this.campanhaService.listPublic(params).subscribe({
      next: (res) => {
        let data: any[] = res?.data ?? [];

        // Filtro defensivo no client-side para garantir refinamento exato por título/nome
        if (hasSearch) {
          const q = this.searchQuery.trim().toLowerCase();
          data = data.filter((c: any) => {
            const tit = (c.titulo || '').toLowerCase();
            const desc = (c.descricao || '').toLowerCase();
            const descCurta = (c.descricaoCurta || '').toLowerCase();
            const criador = (c.utilizadorNome || '').toLowerCase();
            return tit.includes(q) || desc.includes(q) || descCurta.includes(q) || criador.includes(q);
          });
        }

        if (this.filtroTipoFinanciamento) {
          data = data.filter((c: any) => {
            const tipoEnum = Number(c.tipoFinanciamentoENUM);
            return tipoEnum === Number(this.filtroTipoFinanciamento);
          });
        }

        this.totalCampanhasNormais = res?.totalCount || data.length;
        this.carregarFotosNormais(data);
      },
      error: (err) => {
        console.error('Erro ao listar campanhas normais', err);
        this.normalCampanhas = [];
        this.loadingNormais = false;
      },
    });
  }

  carregarFotosNormais(campanhas: any[]): void {
    if (!campanhas || campanhas.length === 0) {
      this.normalCampanhas = [];
      this.loadingNormais = false;
      return;
    }

    const photoRequests = campanhas.map((campanha: any) => {
      const anexos = campanha.anexoCampanha ?? [];
      const imagem = anexos.find(
        (a: any) =>
          (a?.tipoAnexoCampanha?.tipo || '').toString().toLowerCase() ===
          'imagem',
      );

      if (imagem?.anexo?.url) {
        campanha.imgPreview = imagem.anexo.url;
        campanha.fotoUrl = imagem.anexo.url;
        return of(null);
      }

      if (imagem?.anexo?.id) {
        return this.campanhaService.getPhotoCampanhaByID(imagem.anexo.id).pipe(
          map((fotoRes: any) => {
            const url =
              typeof fotoRes === 'string'
                ? fotoRes
                : this.sanitizer.bypassSecurityTrustUrl(
                    URL.createObjectURL(fotoRes),
                  );
            campanha.imgPreview = url;
            campanha.fotoUrl = url;
          }),
          catchError(() => {
            campanha.imgPreview = '/images/campanha-1.png';
            campanha.fotoUrl = '/images/campanha-1.png';
            return of(null);
          }),
        );
      }

      campanha.imgPreview = '/images/campanha-1.png';
      campanha.fotoUrl = '/images/campanha-1.png';
      return of(null);
    });

    forkJoin(photoRequests).subscribe({
      next: () => {
        campanhas.forEach((c) => {
          c.isDestaque = this.featuredCampaignIds.has(Number(c.id));
        });
        if (!this.filtroCategoria) {
          campanhas.sort((a, b) => (b.isDestaque ? 1 : 0) - (a.isDestaque ? 1 : 0));
        }
        this.normalCampanhas = campanhas;
        this.loadingNormais = false;
      },
      error: () => {
        campanhas.forEach((c) => {
          c.isDestaque = this.featuredCampaignIds.has(Number(c.id));
        });
        this.normalCampanhas = campanhas;
        this.loadingNormais = false;
      },
    });
  }

  getCategoryIcon(descricao?: string): string {
    if (!descricao) return 'fas fa-tag';
    const d = descricao.toLowerCase();
    if (d.includes('aliment') || d.includes('comida') || d.includes('fome'))
      return 'fas fa-utensils';
    if (d.includes('saúde') || d.includes('saude') || d.includes('medic'))
      return 'fas fa-heartbeat';
    if (d.includes('educa') || d.includes('escola') || d.includes('ensino'))
      return 'fas fa-graduation-cap';
    if (
      d.includes('tecno') ||
      d.includes('digit') ||
      d.includes('app') ||
      d.includes('inova')
    )
      return 'fas fa-laptop-code';
    if (d.includes('agri') || d.includes('campo') || d.includes('rural'))
      return 'fas fa-seedling';
    if (d.includes('arte') || d.includes('cultur')) return 'fas fa-palette';
    if (d.includes('ambien') || d.includes('ecolog') || d.includes('clima'))
      return 'fas fa-leaf';
    if (d.includes('emerg') || d.includes('social') || d.includes('solidar'))
      return 'fas fa-hands-helping';
    if (
      d.includes('start') ||
      d.includes('negóc') ||
      d.includes('negoc') ||
      d.includes('empree')
    )
      return 'fas fa-lightbulb';
    return 'fas fa-tag';
  }

  getPercent(campanha: any): number {
    return Math.round((campanha.valorArrecado / campanha.metaArrecadar) * 100);
  }

  purposes = [
    {
      faIcon: 'fas fa-heartbeat',
      colorClass: 'green',
      tittle: 'Saúde e Bem-estar',
      desc: 'Contribua para campanhas que promovem o acesso a cuidados de saúde, tratamentos médicos urgentes, saúde mental, nutrição e bem-estar físico nas comunidades mais carentes.',
    },
    {
      faIcon: 'fas fa-lightbulb',
      colorClass: 'amber',
      tittle: 'Startups e Inovação',
      desc: 'Dê impulso a ideias inovadoras e negócios emergentes que querem transformar desafios em oportunidades, criar emprego e dinamizar a economia local com soluções tecnológicas, criativas e sustentáveis.',
    },
    {
      faIcon: 'fas fa-seedling',
      colorClass: 'green',
      tittle: 'Ambiente e Comunidade',
      desc: 'Participe no financiamento de projectos de impacto social e ambiental: desde iniciativas de reflorestação e energias renováveis até campanhas de solidariedade.',
    },
    {
      faIcon: 'fas fa-graduation-cap',
      colorClass: 'blue',
      tittle: 'Educação e Bolsas',
      desc: 'Ajude a financiar bolsas de estudo, construção de infraestruturas escolares, aquisição de materiais didácticos e projectos de alfabetização e formação profissional em todo o país.',
    },
    {
      faIcon: 'fas fa-cubes',
      colorClass: 'purple',
      tittle: 'Outras Causas',
      desc: 'Projetos que não se enquadram nas categorias tradicionais, mas que têm potencial para gerar impacto positivo. Um espaço aberto para ideias inovadoras, causas únicas e iniciativas especiais.',
    },
  ];

  ExperienceUsers = [
    {
      img: 'https://randomuser.me/api/portraits/women/44.jpg',
      name: 'Filipa Anderson',
      location: 'Lisboa, Portugal',
      tittle: 'Apoio que fez a diferença',
      description:
        'Graças à vossa equipa, conseguimos organizar o financiamento do projeto a tempo. Muito obrigada, FundCube!',
    },
    {
      img: 'https://randomuser.me/api/portraits/men/32.jpg',
      name: 'Jordano Vieira',
      location: 'Luanda, Angola',
      tittle: 'Resultados acima do esperado',
      description:
        'A plataforma simplificou a nossa candidatura e poupou-nos semanas de trabalho administrativo.',
    },
    {
      img: 'https://randomuser.me/api/portraits/women/12.jpg',
      name: 'Mariana Pinto',
      location: 'Porto, Portugal',
      tittle: 'Processos mais claros',
      description:
        'Finalmente temos visibilidade sobre prazos e documentos. O acompanhamento foi impecável.',
    },
    {
      img: 'https://randomuser.me/api/portraits/men/76.jpg',
      name: 'Carlos Matola',
      location: 'Maputo, Moçambique',
      tittle: 'Parceria de confiança',
      description:
        'O FundCube ajudou-nos a estruturar o dossiê e preparou-nos para auditorias sem stress.',
    },
    {
      img: 'https://randomuser.me/api/portraits/women/68.jpg',
      name: 'Ana Beatriz Souza',
      location: 'São Paulo, Brasil',
      tittle: 'Do caos à organização',
      description:
        'Centralizámos tudo num só lugar e reduzimos falhas de comunicação entre equipas.',
    },
    // {
    //   img: 'https://randomuser.me/api/portraits/men/15.jpg',
    //   name: 'Miguel Azevedo',
    //   location: 'Braga, Portugal',
    //   tittle: 'Objetivo alcançado',
    //   description:
    //     'Com o vosso apoio, submetemos a candidatura e garantimos a aprovação do financiamento.',
    // },
    // {
    //   img: 'https://randomuser.me/api/portraits/women/22.jpg',
    //   name: 'Sara Nascimento',
    //   location: 'Huambo, Angola',
    //   tittle: 'Experiência excelente',
    //   description:
    //     'Ferramentas simples, relatórios claros e uma equipa sempre disponível para ajudar.',
    // },
    // {
    //   img: 'https://randomuser.me/api/portraits/men/5.jpg',
    //   name: 'Rui Monteiro',
    //   location: 'Faro, Portugal',
    //   tittle: 'Suporte impecável',
    //   description:
    //     'Cada etapa foi explicada com clareza. Recomendo a quem quer profissionalizar processos.',
    // },
  ];
}
